import { Trans, useLingui } from '@lingui/react/macro';

import Paragraph from '@/design_system/Typography/Paragraph';
import IconStore from '@/icons/Store.svg';
import IconStoreRepair from '@/icons/StoreRepair.svg';
import { ArticleConditionForm } from '@/models/article';
import { useDatabaseTranslation } from '@/services/i18n';

import { ConditionFormQuestion, getSubQuestion } from './ConditionQuestion';

export const ConditionQuestionReadonlyAnswers = ({
  question,
  action,
  articleConditionForms,
}: {
  question?: ConditionFormQuestion;
  action?: { stableId: string };
  articleConditionForms: ArticleConditionForm[];
}) => {
  const readonlyArticleConditionForms = articleConditionForms.filter(
    (articleConditionForm) => !!articleConditionForm.completedAt
  );

  const isEditable = !!articleConditionForms.find(
    (articleConditionForm) => !articleConditionForm.completedAt
  );

  if (!readonlyArticleConditionForms.length) {
    return null;
  }

  return (
    <div style={{ padding: isEditable ? '8px 0 0 12px' : '0 0 0 12px' }}>
      {readonlyArticleConditionForms.map((articleConditionForm) => {
        const context = articleConditionForm.articleConditions
          .find((articleCondition) =>
            question
              ? articleCondition.questionId === question.id
              : articleCondition.articleAction?.stableId === action?.stableId
          )
          ?.context?.trim();

        // If a radio question answer showed a subQuestion,
        // in read-only mode we only display the answer to the subQuestion
        const questionToDisplayTheAnswer = question
          ? (getSubQuestion({ question, articleConditionForm }) ?? question)
          : undefined;

        return (
          <Paragraph
            size={isEditable ? '200' : '100'}
            color={isEditable ? 'secondary' : 'primary'}
            key={articleConditionForm.id}
          >
            {articleConditionForms.length > 1 && (
              <ConditionQuestionReadonlyAnswerer articleConditionForm={articleConditionForm} />
            )}
            {questionToDisplayTheAnswer?.type === 'radio' && (
              <ConditionQuestionRadioReadonlyAnswer
                question={questionToDisplayTheAnswer}
                articleConditionForm={articleConditionForm}
              />
            )}
            {questionToDisplayTheAnswer?.type === 'checkbox' && (
              <ConditionQuestionCheckboxReadonlyAnswer
                question={questionToDisplayTheAnswer}
                articleConditionForm={articleConditionForm}
              />
            )}
            {questionToDisplayTheAnswer?.type === 'text-and-select' && (
              <ConditionQuestionTextAndSelectReadonlyAnswer
                question={questionToDisplayTheAnswer}
                articleConditionForm={articleConditionForm}
              />
            )}
            {questionToDisplayTheAnswer?.type === 'multi-quantifiable-select' && (
              <ConditionQuestionMultiQuantifiableSelectReadonlyAnswer
                question={questionToDisplayTheAnswer}
                articleConditionForm={articleConditionForm}
              />
            )}
            {!!action && (
              <ActionQuestionReadonlyAnswer
                action={action}
                articleConditionForm={articleConditionForm}
              />
            )}
            {!!context && <> • &quot;{context}&quot;</>}
          </Paragraph>
        );
      })}
    </div>
  );
};

const ConditionQuestionReadonlyAnswerer = ({
  articleConditionForm,
}: {
  articleConditionForm: ArticleConditionForm;
}) => {
  const { t } = useLingui();

  if (articleConditionForm.workshop) {
    return (
      <>
        <IconStoreRepair
          style={{
            display: 'inline',
            fontSize: '1.25rem',
            verticalAlign: 'text-bottom',
          }}
        />{' '}
        {articleConditionForm.workshop.name}
        {t({ id: '_general.colon', message: ':' })}{' '}
      </>
    );
  } else if (articleConditionForm.store) {
    return (
      <>
        <IconStore
          style={{
            display: 'inline',
            fontSize: '1.25rem',
            verticalAlign: 'text-bottom',
          }}
        />{' '}
        {articleConditionForm.store.name}
        {t({ id: '_general.colon', message: ':' })}{' '}
      </>
    );
  } else {
    return null;
  }
};

const ConditionQuestionRadioReadonlyAnswer = ({
  question,
  articleConditionForm,
}: {
  question: Extract<ConditionFormQuestion, { type: 'radio' }>;
  articleConditionForm: ArticleConditionForm;
}) => {
  const { _db } = useDatabaseTranslation();

  const articleCondition = articleConditionForm.articleConditions.find(
    ({ questionId }) => questionId === question.id
  );

  const selectedAnswer = question.answers?.find(
    (answer) => answer.id === articleCondition?.answerId
  );

  return selectedAnswer ? _db(selectedAnswer.text) : '-';
};

const ConditionQuestionCheckboxReadonlyAnswer = ({
  question,
  articleConditionForm,
}: {
  question: Extract<ConditionFormQuestion, { type: 'checkbox' }>;
  articleConditionForm: ArticleConditionForm;
}) => {
  const { _db } = useDatabaseTranslation();

  const selectedAnswerIds =
    articleConditionForm.articleConditions
      ?.filter((articleCondition) => articleCondition.questionId === question.id)
      ?.map((articleCondition) => articleCondition.answerId) ?? [];

  return question.answers
    ?.filter((answer) => selectedAnswerIds.includes(answer.id))
    .map((answer) => {
      return _db(answer.text);
    })
    .join(', ');
};

const ConditionQuestionTextAndSelectReadonlyAnswer = ({
  question,
  articleConditionForm,
}: {
  question: Extract<ConditionFormQuestion, { type: 'text-and-select' }>;
  articleConditionForm: ArticleConditionForm;
}) => {
  const { _db } = useDatabaseTranslation();

  const answerData = articleConditionForm.articleConditions.find(
    ({ questionId }) => questionId === question.id
  )?.answerData;

  const selectValue = question.config.select.options.find(
    (options) => options.value === answerData?.select
  );

  return (
    <>
      {!!answerData?.text && <>&quot;{answerData.text}&quot;</>}{' '}
      {!!selectValue && <>{_db(selectValue.label)}</>}
    </>
  );
};

const ConditionQuestionMultiQuantifiableSelectReadonlyAnswer = ({
  question,
  articleConditionForm,
}: {
  question: Extract<ConditionFormQuestion, { type: 'multi-quantifiable-select' }>;
  articleConditionForm: ArticleConditionForm;
}) => {
  const { _db } = useDatabaseTranslation();

  const answersWithQuantity = articleConditionForm.articleConditions
    .filter((articleCondition) => articleCondition.questionId === question.id)
    .map((articleCondition) => {
      const answer = question.answers?.find((option) => option.id === articleCondition.answerId);

      if (!answer) {
        return null;
      }

      const quantity = articleCondition.answerData?.quantity ?? 1;

      return quantity > 1 ? `${_db(answer.text)} (x${quantity})` : _db(answer.text);
    })
    .filter((answer) => answer !== null)
    .join(', ');

  return answersWithQuantity;
};

const ActionQuestionReadonlyAnswer = ({
  action,
  articleConditionForm,
}: {
  action: { stableId: string };
  articleConditionForm: ArticleConditionForm;
}) => {
  const articleCondition = articleConditionForm.articleConditions.find(
    ({ articleAction }) => articleAction?.stableId === action.stableId
  );

  const value = articleCondition?.answerData?.yesNoRadioValue;

  if (!value) {
    return '-';
  }

  return value === 'yes' ? (
    <Trans id="design-system.radio.yes">Yes</Trans>
  ) : (
    <Trans id="design-system.radio.no">No</Trans>
  );
};
